import React, {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  ReactNode, Ref,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

import { Container, ErrorContainer, HiddenInput, InputContainer, Label } from './forms/Input';
import { useUserContext } from '../contexts/UserContext';

import { ULogApplication, ULogSeverity, ULogTag, logSumoEvent } from 'Common/src/api/SumoLogicApi';

const ExpandingTextInput = ({ value, label, placeholder, textColor, onChange, onSubmit, onInvalid, charLimit, required, icon, noVertMargin, minHeight, error, errorRef }: {
  value: string,
  label?: string;
  placeholder?: string,
  textColor: string,
  onChange: (value: string) => void,
  onInvalid?: (e: FormEvent) => void,
  onSubmit?: (value?: string) => void;
  charLimit?: number;
  required?: boolean;
  icon?: ReactNode;
  noVertMargin?: boolean;
  minHeight?: number;
  error?: string;
  errorRef?: Ref<HTMLDivElement>;
}) => {
  const userContext = useUserContext();

  const [charCount, setCharCount] = useState<number | undefined>(charLimit);
  const [text, setText] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (divRef.current && (!divRef.current.innerText || value === '')) {
      divRef.current.innerText = value;
      onTextChange(value);
    }
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit !== undefined) {
      onSubmit(value);
      e.preventDefault();
    }
  };

  const onTextChange = (text: string) => {
    const trimmedText = text.replace(/\s*\n\s*\n\s*/g, '\n\n');
    onChange(trimmedText);
    if (charLimit !== undefined) {
      setCharCount(charLimit - trimmedText.length);
    }
    setText(trimmedText);
    setInvalid(false);
  };

  const handleCopy = (e: ClipboardEvent) => {
    const text = window.getSelection()?.toString();
    if (text) {
      e.clipboardData.setData('text/plain', text);
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain');
    try {
      if (document.execCommand !== undefined) {
        document.execCommand('insertText', false, text);
        e.preventDefault();
      }
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.WARN,
        userId: userContext.id,
        tag: ULogTag.Browser,
        message: `[ExpandingTextInput] handlePaste - "${text}" Error: ${JSON.stringify(e)}`
      });
    }
    setInvalid(false);
  };

  const onInputInvalid = (e: FormEvent) => {
    e.preventDefault();
    setInvalid(true);
    if (onInvalid) {
      onInvalid(e);
    }
  }

  let charLimitText: ReactNode = null;
  if (charCount !== undefined) {
    charLimitText = <CharacterCount $charCount={charCount}>{charCount} characters left</CharacterCount>;
  }

  const filled = text !== '' && text !== '\n';

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    setInvalid(false);
    onTextChange(e.currentTarget.innerText);
  }

  return <Container ref={errorRef} $growing={true} $noVertMargin={noVertMargin}>
    {icon !== undefined
      ? <>
        <IconAndInputContainer>
          <InputContainer $filled={filled || isFocused || placeholder !== undefined} $smaller={true}
                          $minHeight={minHeight}>
            <TextInput
              data-ph={placeholder}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onCopy={handleCopy}
              onPaste={handlePaste}
              ref={divRef}
              contentEditable
              $color={textColor}
              $smaller={true}
              onInput={handleInput}>
            </TextInput>
            <Label>{label}</Label>
          </InputContainer>
          {icon}
        </IconAndInputContainer>
      </>
      : <InputContainer $filled={filled || isFocused || placeholder !== undefined} $minHeight={minHeight}>
        <TextInput
          data-ph={placeholder}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onCopy={handleCopy}
          onPaste={handlePaste}
          ref={divRef}
          contentEditable
          $color={textColor}
          onInput={handleInput}>
        </TextInput>
        <Label>{label}</Label>
      </InputContainer>}
    <HiddenInput defaultValue={text} required={required} onInvalid={onInputInvalid}/>
    {charLimitText}
    {error ? <ErrorContainer>{error}</ErrorContainer> : invalid ?
      <ErrorContainer>This is a required field</ErrorContainer> : null}

  </Container>;
};

const TextInput = styled.div<{ $color: string, $smaller?: boolean }>`
  color: ${({ $color }) => $color};
  outline: 0;
  padding-bottom: 5px;
  font-size: 24px;
  margin: ${({ $smaller }) => ($smaller ? '25px 5px 0 17px' : '25px 25px 0 17px')};

  &:empty:not(:focus):before {
    content: attr(data-ph);
    opacity: 0.6;
    pointer-events: none;
  }
`;

const IconAndInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;
`

const CharacterCount = styled.div<{ $charCount: number }>`
  color: ${({ $charCount }) => ($charCount <= 0 ? 'var(--shine-system-error-color)' : 'var(--shine-text-color)')}; // Change color when 0 or negative
  text-align: right; // Align to the right (or left, as you prefer)
  margin: 0 10px 5px 0;
  font-size: 12px;
`;


export default ExpandingTextInput;
